<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import PermissionLock from '../../../PermissionLock.vue';
import { useMyOrganizations } from '@ilteducation/my-account';
import { AvailableIdentifier, useUserIdentifiers } from '@ilteducation/user-identifiers';
import { useEditUser, CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkUserUpdatePermission } from '@ilteducation/customer-mgmt-permissions';

import { useLocale } from '../../../../composables/useLocale';
import useModal, { modals } from '../../../../composables/useModal';
import { usePermissions } from '../../../../composables/usePermissions';
import { useSidebarUser } from '../../../../composables/useSidebarUser';
import { useEscalateLogin } from '../../../../composables/useEscalateLogin';

import Modal from '../../../Modal.vue';
import IdentifierRow from './IdentifierRow.vue';
import PasswordTextField from './PasswordTextField.vue';
import UsernameTextField from './UsernameTextField.vue';
import { useUserContext } from '@ilteducation/ilt-auth';
import { storeToRefs } from 'pinia';

const { t } = useLocale();

type Props = {
  name: string;
};

defineProps<Props>();

const { updateUser, isLoading: isUpdatingUsername } = useEditUser();
const { getUser, isLoading: isFetchingUser, user } = useSidebarUser();
const userContext = useUserContext();
const { isMFA } = storeToRefs(userContext);
const {
  isOpen,
  open: openManageUserLoginInfoModal,
  close: closeManageUserLoginInfoModal,
} = useModal(modals.MANAGE_USER_LOGIN_INFO);
const { open: openCreateLoginCardModal } = useModal(modals.CREATE_LOGIN_CARD);
const { open: openRemoveQrCodeConfirmation, close: closeRemoveQrCodeConfirmation } = useModal(
  modals.CONFIRM_DELETE_USER_QR_CODE,
);
const { availableIdentifiers } = useUserIdentifiers();
const { organizations } = useMyOrganizations();

const {
  permissionContext,
  getCallerAssociations,
  getGroupMemberships,
  createGetTargetAssociations,
  reporterWithReason,
} = usePermissions();

const checkUserPropertyUpdatedable = (
  propertiesToCheck: Partial<CustomerMgmtTypes.UpdateUserRequest>,
): Promise<ReturnType<typeof reporterWithReason>> => {
  if (!user.value) {
    return Promise.resolve({ allowed: false, reason: 'not_permitted' });
  }

  return checkUserUpdatePermission(
    permissionContext.value,
    {
      userId: user.value.uid,
      ...propertiesToCheck,
    },
    getCallerAssociations,
    createGetTargetAssociations(user.value),
    getGroupMemberships,
    reporterWithReason,
  );
};

const isUsernameEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
const isUserPasswordEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
const isQrTokenEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
const isIdentifierUpdateEditableCheck = ref<Record<string, ReturnType<typeof reporterWithReason>>>(
  availableIdentifiers.value.reduce<Record<string, ReturnType<typeof reporterWithReason>>>(
    (acc, curr) => {
      acc[curr.type] = { allowed: true, reason: undefined };
      return acc;
    },
    { sms: { allowed: true, reason: undefined } },
  ),
);
const allowedToAddIdentifiers = ref<AvailableIdentifier[]>([]);

watch(
  [user, isOpen],
  async (newUser, newIsOpen) => {
    if (!newIsOpen) {
      return;
    }
    if (newUser) {
      allowedToAddIdentifiers.value = [];
      checkUserPropertyUpdatedable({ userName: 'placeholder' }).then((result) => {
        isUsernameEditableCheck.value = result;
      });
      checkUserPropertyUpdatedable({ password: 'placeholder' }).then((result) => {
        isUserPasswordEditableCheck.value = result;
      });
      checkUserPropertyUpdatedable({ qrToken: 'placeholder' }).then((result) => {
        isQrTokenEditableCheck.value = result;
      });
      availableIdentifiers.value.forEach(async (identifier) => {
        checkUserPropertyUpdatedable({
          identifiers: { [identifier.type]: 'placeholder' },
        }).then((result) => {
          isIdentifierUpdateEditableCheck.value[identifier.type] = result;
          if (!identifier.readonly && result.allowed) {
            allowedToAddIdentifiers.value.push(identifier);
          }
        });
      });
    }
  },
  { immediate: true },
);

const identifierToDeleteByType = ref<Record<string, boolean>>({});

type NewIdentifier = Pick<CustomerMgmtTypes.IdentifierOutput, 'type' | 'value'> & {
  source?: CustomerMgmtTypes.SourceOutput;
};

const newIdentifiers = ref<NewIdentifier[]>([]);
const addIdentifierLine = (type: string) => {
  newIdentifiers.value.push({ type, value: '' });
};

const userIdentifiers = computed(() => {
  const iconByType = availableIdentifiers.value.reduce<Record<string, string | undefined>>((acc, curr) => {
    acc[curr.type] = curr.iconDataUri;
    return acc;
  }, {});

  const validIdentifiers = (user.value?.identifiers ?? []).filter((id) => !!id.value);
  return [...validIdentifiers, ...newIdentifiers.value].map((id) => {
    return {
      ...id,
      iconDataUri: iconByType[id.type],
    };
  });
});

const identifiersAvailableToAdd = computed(() => {
  if (!user.value) {
    return [];
  }

  return allowedToAddIdentifiers.value?.filter((id) => !userIdentifiers.value.find((pId) => pId.type === id.type));
});

const identifiersAvailableWithMFA = computed(() => availableIdentifiers.value.some((id) => id.requiresMFA));

const isQrCodeUpdating = ref<boolean>(false);

const addQrCode = () => {
  if (!user.value) {
    return;
  }
  closeManageUserLoginInfoModal();
  openCreateLoginCardModal({
    createQrCodeCheck: true,
    onClose: () => {
      openManageUserLoginInfoModal();
    },
  });
};

const removeQrCode = async () => {
  if (!user.value) {
    return;
  }
  closeRemoveQrCodeConfirmation();
  isQrCodeUpdating.value = true;
  await updateUser(user.value.uid, {
    qrToken: null,
  });
  await getUser(user.value.uid, true);
  isQrCodeUpdating.value = false;

  openManageUserLoginInfoModal();
};

const onOpenRemoveQrCodeConfirmation = () => {
  if (!user.value) return;
  closeManageUserLoginInfoModal();
  openRemoveQrCodeConfirmation();
};

const isLoading = computed(() => {
  return isUpdatingUsername.value || isFetchingUser.value || isQrCodeUpdating.value;
});

const onUserUpdated = async () => {
  if (user.value) {
    await getUser(user.value.uid, true);
  }
};

const { requestEscalatedLogin } = useEscalateLogin();
const onSecureLoginClick = () => {
  requestEscalatedLogin();
};

const onIdentifierUpdate = async (type: string) => {
  await onUserUpdated();
  newIdentifiers.value = newIdentifiers.value.filter((id) => id.type !== type);
};

defineExpose({
  removeQrCode: removeQrCode,
});
</script>
<template>
  <Modal
    :name="name"
    :title="t('lcp.manage_login')"
    :action-label="t('lcp.close')"
    :action="closeManageUserLoginInfoModal"
    :show-cancel-action="false"
    :secondary-action="addQrCode"
    :secondary-action-label="t('lcp.create_new_login_card')"
    secondary-action-variant="tonal"
  >
    <v-row>
      <v-col>
        <UsernameTextField
          v-if="user"
          :model-value="user?.userName ?? ''"
          :user="user"
          :loading="isLoading"
          :disabled="!isUsernameEditableCheck.allowed"
          :organizations="organizations"
          @updated="onUserUpdated"
        />
      </v-col>
      <v-col v-if="!isUsernameEditableCheck.allowed" cols="auto" class="d-flex align-center justify-end pl-0">
        <PermissionLock :reason="isUsernameEditableCheck.reason" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <PasswordTextField
          v-if="user"
          :user="user"
          :loading="isLoading"
          :disabled="!isUserPasswordEditableCheck.allowed"
          :organizations="organizations"
        />
      </v-col>
      <v-col v-if="!isUserPasswordEditableCheck.allowed" cols="auto" class="d-flex align-center justify-end pl-0">
        <PermissionLock :reason="isUserPasswordEditableCheck.reason" />
      </v-col>
    </v-row>

    <v-row v-if="userIdentifiers.length > 0 || identifiersAvailableToAdd.length > 0">
      <v-col>
        <v-divider></v-divider>
      </v-col>
    </v-row>

    <!-- Identifier Inputs -->
    <template v-for="id in userIdentifiers" :key="id.type + id.value">
      <IdentifierRow
        v-if="user"
        :userUid="user.uid"
        :model-value="id.value"
        :type="id.type"
        :disabled="isLoading"
        :show-confirm="identifierToDeleteByType[id.type]"
        :forbiddenError="
          isIdentifierUpdateEditableCheck?.[id.type] && isIdentifierUpdateEditableCheck[id.type].allowed
            ? false
            : isIdentifierUpdateEditableCheck[id.type].reason
        "
        @updated="onIdentifierUpdate(id.type)"
      ></IdentifierRow>
    </template>

    <!-- Add Identifier Chips  -->
    <v-row>
      <v-col cols="12" class="d-flex flex-wrap ga-2">
        <v-chip
          v-for="id in identifiersAvailableToAdd"
          :key="id.type"
          role="button"
          :disabled="isLoading"
          @click="addIdentifierLine(id.type)"
          prepend-icon="mdi-plus"
        >
          {{ id.name }}
        </v-chip>
      </v-col>
    </v-row>
    <!-- Add Identifier Chips End  -->

    <v-row v-if="!isMFA && identifiersAvailableWithMFA" dense>
      <v-col>
        <v-btn variant="text" append-icon="mdi-lock" @click="onSecureLoginClick">
          {{ t('lcp.secure_login') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-divider> </v-divider>
      </v-col>
    </v-row>

    <!-- QR Manage TODO -->
    <v-row>
      <v-col v-if="user">
        <v-row class="align-center">
          <v-col cols="auto" class="pr-1">
            <v-icon v-if="user.qrEnabled" size="x-small" icon="mdi-check-circle" color="success"></v-icon>
          </v-col>
          <v-col cols="auto" class="text-subtitle-1 font-weight-bold px-1">{{ t('lcp.qrcode') }}</v-col>
          <v-col>
            <v-row v-if="user.qrEnabled">
              <v-col cols="auto">
                <v-chip
                  role="button"
                  :disabled="isLoading || !isQrTokenEditableCheck.allowed"
                  prepend-icon="mdi-window-close"
                  @click="onOpenRemoveQrCodeConfirmation"
                  >{{ t('lcp.remove') }}</v-chip
                >
              </v-col>
              <v-col v-if="!isQrTokenEditableCheck.allowed" cols="auto" class="px-0 d-flex align-center">
                <PermissionLock :reason="isQrTokenEditableCheck.reason" />
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="auto">
                <v-chip
                  role="button"
                  :disabled="isLoading || !isQrTokenEditableCheck.allowed"
                  prepend-icon="mdi-plus"
                  @click="addQrCode"
                  >{{ t('lcp.add_qr_code') }}</v-chip
                >
              </v-col>
              <v-col v-if="!isQrTokenEditableCheck.allowed" cols="auto" class="px-0 d-flex align-center">
                <PermissionLock :reason="isQrTokenEditableCheck.reason" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col> {{ t('lcp.qr_code_help') }} </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- QR Manage End -->
  </Modal>
</template>
